'use client';

import { usePlayerPlinthMaterial } from '@/features/lobby/board-canvas/player-plinth-preference';
import type { PlinthMaterialId } from '@/features/lobby/theming/plinths/types';
import { useTutorialOptional } from './TutorialContext';

/**
 * Returns the player-plinth material every viewer-owned tile should
 * paint with. Mirrors `usePlayerPlinthMaterial` 1:1 in production,
 * but inside the `/tutorial` route returns the script's hard-coded
 * override (currently `'obsidian'`) so the post-buyout slab regrows
 * with the dramatic black material the tutorial copy promises —
 * regardless of whatever the viewer happens to have saved as their
 * persisted preference.
 *
 * Wire this hook into anywhere `usePlayerPlinthMaterial()` is consumed
 * so the override propagates without further plumbing:
 *   - `BoardCanvas2D` / `LobbySeatTile2D`
 *   - `BoardCanvas3D` -> `TileMaterialCapOverlay`
 *   - `BoardSeatTooltipShell` (avatar plinth in hover tooltips)
 *   - `LobbyClient` (passes `playerPlinthId` down)
 *
 * The Profile page intentionally does NOT use this hook — the picker
 * there should keep showing the user's actual saved choice.
 */
export function useEffectivePlayerPlinth(): PlinthMaterialId {
  const persisted = usePlayerPlinthMaterial();
  const tutorial = useTutorialOptional();
  return tutorial?.playerPlinthOverride ?? persisted;
}
